// src/api/posApi.js
//
// API cliente del POS Compacto contra /api/pos/*.
// Agrupa por dominio: empresas, sucursales, catálogo (categorías,
// productos, clientes), parametricas, ubicación.

import { api } from './apiClient';

// ── Empresas ───────────────────────────────────────────────
export const empresas = {
  listar:     (soloActivas = true) => api.get('/api/pos/empresas', { query: { soloActivas } }),
  obtener:    (id)               => api.get(`/api/pos/empresas/${id}`),
  crear:      (dto)              => api.post('/api/pos/empresas', dto),
  actualizar: (id, dto)          => api.put(`/api/pos/empresas/${id}`, dto),
};

// ── Sucursales ─────────────────────────────────────────────
export const sucursales = {
  listar:     (empresaId, soloActivas = true) =>
    api.get('/api/pos/sucursales', { query: { empresaId, soloActivas } }),
  obtener:    (id)         => api.get(`/api/pos/sucursales/${id}`),
  crear:      (dto)        => api.post('/api/pos/sucursales', dto),
  actualizar: (id, dto)    => api.put(`/api/pos/sucursales/${id}`, dto),
  cambiarEstado: (id, activa) => api.patch(`/api/pos/sucursales/${id}/estado`, activa),
};

// ── Catálogo ───────────────────────────────────────────────
export const catalogo = {
  categorias: {
    listar:     (empresaId, seccionId) =>
      api.get('/api/pos/catalogo/categorias', { query: { empresaId, seccionId } }),
    crear:      (dto)     => api.post('/api/pos/catalogo/categorias', dto),
    actualizar: (id, dto) => api.put(`/api/pos/catalogo/categorias/${id}`, dto),
    eliminar:   (id)      => api.delete(`/api/pos/catalogo/categorias/${id}`),
  },
  secciones: {
    listar: (empresaId) => api.get('/api/pos/catalogo/secciones', { query: { empresaId } }),
    crear:  (dto)       => api.post('/api/pos/catalogo/secciones', dto),
  },
  productos: {
    listar:     (empresaId, params) =>
      api.get('/api/pos/catalogo/productos', { query: { empresaId, ...(params || {}) } }),
    obtener:    (id)     => api.get(`/api/pos/catalogo/productos/${id}`),
    crear:      (dto)    => api.post('/api/pos/catalogo/productos', dto),
    actualizar: (id, dto)=> api.put(`/api/pos/catalogo/productos/${id}`, dto),
    eliminar:   (id)     => api.delete(`/api/pos/catalogo/productos/${id}`),
  },
  clientes: {
    listar:     (empresaId, params) =>
      api.get('/api/pos/catalogo/clientes', { query: { empresaId, ...(params || {}) } }),
    obtener:    (id)     => api.get(`/api/pos/catalogo/clientes/${id}`),
    crear:      (dto)    => api.post('/api/pos/catalogo/clientes', dto),
    actualizar: (id, dto)=> api.put(`/api/pos/catalogo/clientes/${id}`, dto),
  },
};

// ── Parametricas (dropdowns compartidos) ───────────────────
export const parametricas = {
  monedas:            () => api.get('/api/pos/parametricas/monedas'),
  regimenesTributarios: () => api.get('/api/pos/parametricas/regimenes-tributarios'),
  tarifasIva:         () => api.get('/api/pos/parametricas/tarifas-iva'),
  unidadesMedida:     () => api.get('/api/pos/parametricas/unidades-medida'),
};

// ── Catálogo maestro unificado (mig 038) ────────────────────
// Fuente única de categorías: HolaVeci mobile y POS Compacto ven
// las mismas etiquetas. Estructura padre → hija.
export const categoriasCatalogo = {
  listar: (empresaId, soloActivas = true) =>
    api.get('/api/pos/catalogo/categorias-catalogo', {
      query: { empresaId, soloActivas },
    }),
  crear:      (dto) => api.post('/api/pos/catalogo/categorias-catalogo', dto),
  actualizar: (id, dto) => api.put(`/api/pos/catalogo/categorias-catalogo/${id}`, dto),
  eliminar:   (id) => api.delete(`/api/pos/catalogo/categorias-catalogo/${id}`),
};

// ── Movimientos de inventario (C4.4) ────────────────────────
export const movimientos = {
  historial: (empresaId, filtros = {}) =>
    api.get('/api/pos/inventario/historial', { query: { empresaId, ...filtros } }),
  kardex: (productoId, empresaId, opts = {}) =>
    api.get(`/api/pos/inventario/kardex/${productoId}`, { query: { empresaId, ...opts } }),
  stockPorAlmacen: (productoId, empresaId) =>
    api.get(`/api/pos/inventario/stock-por-almacen/${productoId}`, { query: { empresaId } }),
  entrada:  (empresaId, dto) => api.post(`/api/pos/inventario/entrada?empresaId=${empresaId}`, dto),
  salida:   (empresaId, dto) => api.post(`/api/pos/inventario/salida?empresaId=${empresaId}`, dto),
  ajuste:   (empresaId, dto) => api.post(`/api/pos/inventario/ajuste?empresaId=${empresaId}`, dto),
  transferencia: (empresaId, dto) =>
    api.post(`/api/pos/inventario/transferencia?empresaId=${empresaId}`, dto),
  aprobarTransferencia:  (empresaId, id) =>
    api.post(`/api/pos/inventario/transferencia/${id}/aprobar?empresaId=${empresaId}`, {}),
  rechazarTransferencia: (empresaId, id, motivo) =>
    api.post(`/api/pos/inventario/transferencia/${id}/rechazar?empresaId=${empresaId}`, { motivo }),
  pendientes: (empresaId) =>
    api.get('/api/pos/inventario/pendientes', { query: { empresaId } }),
};

// ── Listas de precios (C4.4) ────────────────────────────────
export const listasPrecios = {
  listar: (empresaId) => api.get('/api/pos/catalogo/listas-precios', { query: { empresaId } }),
  obtener: (empresaId, id) => api.get(`/api/pos/catalogo/listas-precios/${id}`, { query: { empresaId } }),
  detalle: (empresaId, id) => api.get(`/api/pos/catalogo/listas-precios/${id}/detalle`, { query: { empresaId } }),
  crear:   (empresaId, dto) => api.post(`/api/pos/catalogo/listas-precios?empresaId=${empresaId}`, dto),
  duplicar: (empresaId, dto) => api.post(`/api/pos/catalogo/listas-precios/duplicar?empresaId=${empresaId}`, dto),
  actualizarIndividual: (empresaId, listaId, productoId, nuevoPrecio) =>
    api.patch(`/api/pos/catalogo/listas-precios/${listaId}/detalle/${productoId}?empresaId=${empresaId}`,
      { nuevoPrecio }),
  aplicarDescuento: (empresaId, listaId, dto) =>
    api.post(`/api/pos/catalogo/listas-precios/${listaId}/aplicar-descuento?empresaId=${empresaId}`, dto),
  asignaciones: (empresaId, almacenId) =>
    api.get('/api/pos/catalogo/asignaciones', { query: { empresaId, almacenId } }),
  asignar: (empresaId, dto) =>
    api.post(`/api/pos/catalogo/asignaciones?empresaId=${empresaId}`, dto),
  desactivar: (empresaId, id) =>
    api.patch(`/api/pos/catalogo/asignaciones/${id}/desactivar?empresaId=${empresaId}`, {}),
};

// ── Clientes (C5) ───────────────────────────────────────────
export const clientes = {
  listar: (empresaId, filtros = {}) =>
    api.get('/api/pos/clientes', { query: { empresaId, ...filtros } }),
  obtener: (empresaId, id) =>
    api.get(`/api/pos/clientes/${id}`, { query: { empresaId } }),
  consumidorFinal: (empresaId) =>
    api.get('/api/pos/clientes/consumidor-final', { query: { empresaId } }),
  porDocumento: (empresaId, doc) =>
    api.get(`/api/pos/clientes/por-documento/${encodeURIComponent(doc)}`, { query: { empresaId } }),
  crear: (empresaId, dto) => api.post(`/api/pos/clientes?empresaId=${empresaId}`, dto),
  actualizar: (empresaId, id, dto) => api.put(`/api/pos/clientes/${id}?empresaId=${empresaId}`, dto),
  eliminar: (empresaId, id) => api.delete(`/api/pos/clientes/${id}?empresaId=${empresaId}`),
  abonar: (empresaId, id, monto, notas) =>
    api.post(`/api/pos/clientes/${id}/abono?empresaId=${empresaId}`, { monto, notas }),
  historial: (empresaId, id, page = 1, pageSize = 20) =>
    api.get(`/api/pos/clientes/${id}/historial`, { query: { empresaId, page, pageSize } }),
};

// ── Grupos de cliente (C5) ─────────────────────────────────
export const gruposCliente = {
  listar: (empresaId) => api.get('/api/pos/grupos-cliente', { query: { empresaId } }),
  crear: (empresaId, dto) => api.post(`/api/pos/grupos-cliente?empresaId=${empresaId}`, dto),
  actualizar: (empresaId, id, dto) => api.put(`/api/pos/grupos-cliente/${id}?empresaId=${empresaId}`, dto),
  eliminar: (empresaId, id) => api.delete(`/api/pos/grupos-cliente/${id}?empresaId=${empresaId}`),
};

// ── Caja (heredado /api/pos/caja/*) ─────────────────────────
export const caja = {
  // Cajas físicas
  listarCajas: (empresaId, sucursalId) =>
    api.get('/api/pos/caja/cajas', { query: { empresaId, sucursalId: sucursalId || undefined } }),
  crearCaja: (dto) => api.post('/api/pos/caja/cajas', dto),

  // Métodos de pago
  listarMetodosPago: (empresaId) =>
    api.get('/api/pos/caja/metodos-pago', { query: { empresaId } }),
  inicializarMetodosPago: (empresaId) =>
    api.post(`/api/pos/caja/metodos-pago/inicializar?empresaId=${empresaId}`),
  crearMetodoPago: (dto) => api.post('/api/pos/caja/metodos-pago', dto),
  actualizarMetodoPago: (id, dto) => api.put(`/api/pos/caja/metodos-pago/${id}`, dto),

  // Turno
  verificarTurno: (aperturaCajaId) => api.get(`/api/pos/caja/verificar-turno/${aperturaCajaId}`),
  abrir: (dto) => api.post('/api/pos/caja/abrir', dto),
  cerrar: (dto) => api.post('/api/pos/caja/cerrar', dto),
  resumen: (aperturaCajaId) => api.get(`/api/pos/caja/resumen/${aperturaCajaId}`),

  // Movimientos
  registrarMovimiento: (dto) => api.post('/api/pos/caja/movimiento', dto),
  listarMovimientos: (aperturaCajaId, filtros = {}) =>
    api.get(`/api/pos/caja/movimientos/${aperturaCajaId}`, { query: filtros }),

  // Historial de turnos
  historialAperturas: (cajaId, filtros = {}) =>
    api.get(`/api/pos/caja/aperturas/${cajaId}`, { query: filtros }),

  // Autorización con badge (para movimientos SALIDA)
  autorizarAccion: (dto) => api.post('/api/pos/caja/autorizar-accion', dto),
};

// ── Usuarios (heredado /api/pos/usuarios-gestion) ──────────
export const usuarios = {
  listar: (filtros = {}) =>
    api.get('/api/pos/usuarios-gestion', { query: filtros }),
  crear: (dto) =>
    api.post('/api/pos/usuarios-gestion', dto),
  cupoAdmins: (empresaId) =>
    api.get('/api/pos/usuarios-gestion/cupo-admins',
      { query: { empresaId: empresaId || undefined } }),
  actualizarMaxAdmins: (empresaId, maxAdmins) =>
    api.patch(`/api/pos/usuarios-gestion/empresa/${empresaId}/max-admins`,
      { maxAdmins }),
  editar: (id, dto) =>
    api.put(`/api/pos/usuarios-gestion/${id}`, dto),
  bloquear: (id) =>
    api.patch(`/api/pos/usuarios-gestion/${id}/bloquear`),
  desbloquear: (id) =>
    api.patch(`/api/pos/usuarios-gestion/${id}/desbloquear`),
  resetPassword: (id, nuevaPassword) =>
    api.patch(`/api/pos/usuarios-gestion/${id}/reset-password`,
      { nuevaPassword }),
};

// ── Inscripción al módulo POS Compacto ──────────────────────
export const inscripcionPOS = {
  /**
   * Consulta si el usuario logueado tiene la empresa inscrita al POS.
   * Retorna { activo, requiereInscripcion, datosFiscalesPendientes, ... }.
   */
  estado: (empresaId) =>
    api.get('/api/pos/inscripcion/estado',
      { query: { empresaId: empresaId || undefined } }),

  /**
   * Inscribe la empresa al módulo POS. Requiere datos fiscales
   * completos (NIT, DV, razón social, dirección) porque HolaVeci
   * los deja como PENDIENTE.
   */
  inscribir: (dto) =>
    api.post('/api/pos/inscripcion', dto),
};

// ── Perfiles de permisos (heredado /api/pos/perfiles-permisos) ─
export const perfilesPermisos = {
  listar: (empresaId) =>
    api.get('/api/pos/perfiles-permisos',
      { query: { empresaId: empresaId || undefined } }),
  catalogo: () =>
    api.get('/api/pos/perfiles-permisos/catalogo-permisos'),
  crear: (dto) => api.post('/api/pos/perfiles-permisos', dto),
  actualizar: (id, dto) => api.put(`/api/pos/perfiles-permisos/${id}`, dto),
  eliminar: (id) => api.delete(`/api/pos/perfiles-permisos/${id}`),
};

// ── Admin plataforma superadmin (heredado /api/pos/admin-plataforma) ─
export const adminPlataforma = {
  resumen: (dias = 30) =>
    api.get('/api/pos/admin-plataforma/resumen', { query: { dias } }),
  listarEmpresas: (filtros = {}) =>
    api.get('/api/pos/admin-plataforma/empresas', { query: filtros }),
  toggleHolaVeci: (empresaId, activo) =>
    api.patch(`/api/pos/admin-plataforma/empresas/${empresaId}/contrata-holaveci`,
      { activo }),
  togglePOS: (empresaId, activo) =>
    api.patch(`/api/pos/admin-plataforma/empresas/${empresaId}/contrata-pos`,
      { activo }),
};

// ── Domiciliarios (heredado /api/pos/domiciliarios) ─────────
export const domiciliarios = {
  /**
   * Lista domiciliarios de la empresa.
   * tipoAcceso opcional: 'POS_ONLY' | 'HOLAVECI'.
   */
  listar: (empresaId, filtros = {}) =>
    api.get('/api/pos/domiciliarios', { query: { empresaId, ...filtros } }),

  obtener: (id) => api.get(`/api/pos/domiciliarios/${id}`),

  /**
   * Crea un domiciliario. Desde el POS Compacto se crea con
   * tipoAcceso='POS_ONLY' (sin login, solo administrado por el tendero).
   */
  crear: (dto) => api.post('/api/pos/domiciliarios', dto),

  actualizar: (id, dto) => api.put(`/api/pos/domiciliarios/${id}`, dto),

  /** Cambia el estado del domiciliario (ACTIVO, INACTIVO, etc.). */
  cambiarEstado: (id, estado) =>
    api.patch(`/api/pos/domiciliarios/${id}/estado`, { estado }),
};

// ── Reportes / Dashboard (heredado /api/pos/reportes/*) ────
export const reportes = {
  dashboard: (empresaId, sucursalId) =>
    api.get(`/api/pos/reportes/dashboard/${empresaId}`,
      { query: { sucursalId: sucursalId || undefined } }),
  serieDiaria: (empresaId, sucursalId, dias = 30) =>
    api.get('/api/pos/reportes/serie-diaria',
      { query: { empresaId, sucursalId: sucursalId || undefined, dias } }),
  ventasPorCategoria: (empresaId, sucursalId, desde, hasta) =>
    api.get('/api/pos/reportes/ventas-por-categoria',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined } }),
  productosMasVendidos: (empresaId, sucursalId, desde, hasta, top = 10) =>
    api.get('/api/pos/reportes/productos-mas-vendidos',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined, top } }),
  ventasPorHora: (empresaId, sucursalId, fecha) =>
    api.get('/api/pos/reportes/ventas-por-hora',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 fecha: fecha || undefined } }),
  ventasPorMetodoPago: (empresaId, sucursalId, desde, hasta) =>
    api.get('/api/pos/reportes/ventas-por-metodo-pago',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined } }),
  stockCritico: (empresaId, sucursalId) =>
    api.get(`/api/pos/reportes/stock-critico/${empresaId}`,
      { query: { sucursalId: sucursalId || undefined } }),
  clientesFrecuentes: (empresaId, sucursalId, desde, hasta, top = 20) =>
    api.get('/api/pos/reportes/clientes-frecuentes',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined, top } }),
  utilidadPorCategoria: (empresaId, sucursalId, desde, hasta) =>
    api.get('/api/pos/reportes/utilidad-por-categoria',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined } }),
  inventarioEstado: (empresaId, sucursalId) =>
    api.get('/api/pos/reportes/inventario-estado',
      { query: { empresaId, sucursalId: sucursalId || undefined } }),
  domiciliosResumen: (empresaId, sucursalId, desde, hasta) =>
    api.get('/api/pos/reportes/domicilios-resumen',
      { query: { empresaId, sucursalId: sucursalId || undefined,
                 desde: desde || undefined, hasta: hasta || undefined } }),
};

// ── Ventas (usa el VentasController heredado) ──────────────
// El backend heredado tiene un módulo maduro completo. Firma:
//   GET  /api/pos/ventas         → PagedResult<object> { Data, TotalCount, CurrentPage, PageSize }
//                                   filtros: sucursalId, cajaId, aperturaCajaId, estado, desde, hasta
//   GET  /api/pos/ventas/{id}    → VentaDto (incluye Detalles y Pagos)
//   POST /api/pos/ventas         → CrearVentaDto (Items, Pagos, cliente, tipo, etc)
//   POST /api/pos/ventas/{id}/anular → { motivo, autorizadoPorId? } (motivo ≥ 10 chars)
export const ventas = {
  listar: (empresaId, filtros = {}) =>
    api.get('/api/pos/ventas', { query: { empresaId, ...filtros } }),
  obtener: (empresaId, id) => api.get(`/api/pos/ventas/${id}`),
  registrar: (dto) => api.post('/api/pos/ventas', dto),
  anular: (id, motivo, autorizadoPorId) =>
    api.post(`/api/pos/ventas/${id}/anular`, { motivo, autorizadoPorId }),
  validarCredito: (clienteId, montoVenta) =>
    api.get('/api/pos/ventas/validar-credito', { query: { clienteId, montoVenta } }),
  resumenPorCanal: (empresaId, filtros = {}) =>
    api.get('/api/pos/ventas/resumen-por-canal', { query: { empresaId, ...filtros } }),
};

// ── Catálogo (categorías, marcas, productos) ───────────────
export const marcas = {
  listar: (empresaId) => api.get('/api/pos/catalogo/marcas', { query: { empresaId, soloActivas: true, pageSize: 200 } }),
};

export const productos = {
  listar: (empresaId, opciones = {}) =>
    api.get('/api/pos/catalogo/productos', {
      query: { empresaId, ...opciones, pageSize: opciones.pageSize || 100 },
    }),
  obtener: (id) => api.get(`/api/pos/catalogo/productos/${id}`),
  crear:   (dto) => api.post('/api/pos/catalogo/productos', dto),
  actualizar: (id, dto) => api.put(`/api/pos/catalogo/productos/${id}`, dto),
  eliminar:   (id) => api.delete(`/api/pos/catalogo/productos/${id}`),
  buscar: (empresaId, q, top = 20) =>
    api.get('/api/pos/catalogo/productos/buscar', { query: { empresaId, q, top } }),

  /** Productos con stock/precio de un almacén específico (vista operativa). */
  conStockDeAlmacen: (almacenId, opciones = {}) =>
    api.get(`/api/pos/almacen-catalogo/${almacenId}/productos-pos`, {
      query: { pageSize: opciones.pageSize || 100, ...opciones },
    }),

  /** Sube foto del producto (multipart). Devuelve { imagenUrl }. */
  subirFoto: async (productoId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return api.postForm(`/api/pos/catalogo/productos/${productoId}/foto`, fd);
  },
  eliminarFoto: (productoId) => api.delete(`/api/pos/catalogo/productos/${productoId}/foto`),
};

export const almacenes = {
  /** Lista almacenes de una sucursal. */
  listar: (sucursalId) => api.get('/api/pos/almacenes', { query: { sucursalId } }),
};

// ── Ubicación ──────────────────────────────────────────────
export const ubicacion = {
  paises:        (soloActivos = true) => api.get('/api/pos/paises', { query: { soloActivos } }),
  departamentos: (paisId) => api.get('/api/pos/departamentos', { query: { paisId } }),
  municipios:    (departamentoId, buscar) =>
    api.get('/api/pos/municipios', { query: { departamentoId, buscar } }),
  tiposDocumentoIdentidad: () => api.get('/api/pos/tipos-documento-identidad'),

  /**
   * Geocodifica una dirección usando el proxy del backend que llama a
   * Google Geocoding. La clave de Google queda solo en el backend.
   */
  geocodificar: (direccion, municipio, departamento) =>
    api.get('/api/pos/configuracion/geocodificar', {
      query: { direccion, municipio, departamento, pais: 'Colombia' },
    }),
};
